import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { LoadingOutlined, CameraOutlined, UploadOutlined } from "@ant-design/icons";
import { Spin, Dropdown, MenuProps, Tooltip } from "antd";
import cn from "classnames";
import { AttachmentsDetailsError } from "~/typings/types";
import { clientApi } from "~/utils";

import "./UploadFile.modules.scss";
import { ERROR_FIELD_ } from "~/configs/consts";
import { useIsMobile } from "~/hooks";

const antIcon = <LoadingOutlined style={{ fontSize: 18, color: "#3d935e" }} spin />;

const removeNumberByIndex = (index: number, setFileid: Dispatch<SetStateAction<number[]>>) => {
  setFileid(prev => {
    const updated = [...prev];
    updated.splice(index, 1);
    return updated;
  });
};

type Props = {
  id: string;
  count?: number;
  uploadedFiles: File[];
  setUploadedFiles: Dispatch<File[]>;
  isLoadingProps?: boolean;
  setFileid: Dispatch<SetStateAction<number[]>>;
  error?: AttachmentsDetailsError;
  setError: Dispatch<AttachmentsDetailsError>;
  containerHeight?: number;
  setIsFirstLoadingFile: Dispatch<boolean>;
  contentText?: string;
  prefixComponent?: React.ReactNode;
  uploadButtonWidth?: number;
  uploadButtonHeight?: number;
  uploadButtonClassName?: string;
  uploadLabelStyle?: React.CSSProperties;
  uploadButtonStyle?: React.CSSProperties;
  uploadContainerClassName?: string;
  isDisabledButton?: boolean;

  /**
   * simple — кнопка сразу открывает файловый диалог
   * dropdown — кнопка открывает меню (Загрузить файл / Сделать фото)
   */
  mode?: "simple" | "dropdown";
  /** Максимальная длина имени файла перед обрезкой (по умолчанию 30 символов) */
  maxFileNameLength?: number;
  /** Если это дополнительная/параллельная загрузка: */
  isSecondaryLoading?: boolean;
  /** Текст тултипа при secondary loading */
  secondaryLoadingTooltip?: string;
  /** Разрешенные типы файлов (например: ['.jpg', '.png', '.pdf'] или MIME-типы ['image/jpeg', 'image/png']) */
  allowedFileTypes?: string[];
  /** Максимальный размер файла в байтах (например: 5 * 1024 * 1024 для 5MB) */
  maxFileSize?: number;
  /** Минимальный размер файла в байтах (например: 10 * 1024 для 10KB) */
  minFileSize?: number;
  /** Ограничения по размеру изображения в пикселях (проверяются только для файлов-изображений) */
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number;
  maxHeight?: number;
  /** Показывать ли текст с требованиями к файлу в кнопке загрузки */
  showRequirementsInButton?: boolean;
  /** Расположение контента внутри кнопки: column — в столбец, row — в строку */
  buttonLayout?: "column" | "row";
  /** Обязательность загрузки файла */
  required?: boolean;
  /** id уже загруженных файлов (тот же порядок, что и uploadedFiles) — нужны, чтобы при удалении отправить DELETE на бэк */
  fileIds?: number[];
  /** Своё удаление файла на бэке вместо DELETE /helper/file/{id} */
  onDeleteFile?: (fileId: number) => void;
  /**
   * Проверка на дубликат содержимого файла (например, с файлом из соседнего поля —
   * разворот/страница регистрации паспорта). Если возвращает true, файл не добавляется
   * в uploadedFiles и показывается duplicateErrorMessage.
   */
  getDuplicateOf?: (file: File) => Promise<boolean> | boolean;
  duplicateErrorMessage?: string;
};

export const UploadFile = ({
  id,
  count,
  uploadedFiles,
  setUploadedFiles,
  isLoadingProps,
  setFileid,
  error,
  setError,
  containerHeight,
  setIsFirstLoadingFile,
  contentText = "Загрузить",
  prefixComponent,
  uploadButtonWidth = 200,
  uploadButtonHeight = 36,
  uploadButtonClassName,
  uploadLabelStyle,
  uploadButtonStyle,
  uploadContainerClassName,
  isDisabledButton,
  mode = "simple",
  maxFileNameLength = 24,
  isSecondaryLoading = false,
  secondaryLoadingTooltip = "Дождитесь завершения текущей загрузки",
  allowedFileTypes,
  maxFileSize,
  minFileSize,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  showRequirementsInButton = true,
  buttonLayout = "column",
  required = false,
  fileIds,
  onDeleteFile,
  getDuplicateOf,
  duplicateErrorMessage,
}: Props) => {
  const isMobile = useIsMobile();
  const [showMobileTooltip, setShowMobileTooltip] = useState(false);
  const [touchTimer, setTouchTimer] = useState<NodeJS.Timeout | null>(null);

  const inputIdForErrorScroll = error?.has ? `${ERROR_FIELD_}${id}` : undefined;

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (inputIdForErrorScroll) {
      document.getElementById(inputIdForErrorScroll)?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [inputIdForErrorScroll]);

  // Закрытие тултипа при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowMobileTooltip(false);
      }
    };

    if (showMobileTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside as EventListener);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside as EventListener);
      if (touchTimer) clearTimeout(touchTimer);
    };
  }, [showMobileTooltip, touchTimer]);

  const validateFile = (file: File): { isValid: boolean; errorMessage?: string } => {
    if (file.size === 0) {
      return {
        isValid: false,
        errorMessage: `Файл "${file.name}" пустой или поврежден`,
      };
    }

    if (maxFileSize !== undefined && file.size > maxFileSize) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);

      return {
        isValid: false,
        errorMessage: `Размер файла "${file.name}" (${fileSizeMB} МБ) превышает допустимый размер ${maxSizeMB} МБ`,
      };
    }

    if (minFileSize !== undefined && file.size < minFileSize) {
      const minSizeKB = (minFileSize / 1024).toFixed(0);

      return {
        isValid: false,
        errorMessage: `Размер файла "${file.name}" меньше допустимого минимального размера ${minSizeKB} КБ`,
      };
    }

    if (allowedFileTypes && allowedFileTypes.length > 0) {
      const fileName = file.name.toLowerCase();
      const fileExtension = fileName.substring(fileName.lastIndexOf('.'));
      const fileMimeType = file.type.toLowerCase();

      const isTypeAllowed = allowedFileTypes.some(allowedType => {
        const normalizedType = allowedType.toLowerCase();
        if (normalizedType.startsWith('.')) {
          return fileExtension === normalizedType;
        }
        return fileMimeType === normalizedType;
      });

      if (!isTypeAllowed) {
        const allowedExtensions = allowedFileTypes
          .filter(type => type.startsWith('.'))
          .join(', ');
        return {
          isValid: false,
          errorMessage: `Недопустимый формат файла "${file.name}". Разрешенные форматы: ${allowedExtensions || allowedFileTypes.join(', ')}`,
        };
      }
    }

    return { isValid: true };
  };

  const validateImageDimensions = (file: File): Promise<{ isValid: boolean; errorMessage?: string }> => {
    const hasDimensionConstraints = [minWidth, minHeight, maxWidth, maxHeight].some(v => v !== undefined);
    if (!hasDimensionConstraints || !file.type.startsWith('image/')) {
      return Promise.resolve({ isValid: true });
    }

    return new Promise(resolve => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();

      image.onload = () => {
        const { width, height } = image;
        URL.revokeObjectURL(objectUrl);

        if (minWidth !== undefined && width < minWidth) {
          resolve({ isValid: false, errorMessage: `Ширина изображения "${file.name}" (${width}px) меньше минимально допустимой ${minWidth}px` });
          return;
        }
        if (minHeight !== undefined && height < minHeight) {
          resolve({ isValid: false, errorMessage: `Высота изображения "${file.name}" (${height}px) меньше минимально допустимой ${minHeight}px` });
          return;
        }
        if (maxWidth !== undefined && width > maxWidth) {
          resolve({ isValid: false, errorMessage: `Ширина изображения "${file.name}" (${width}px) превышает максимально допустимую ${maxWidth}px` });
          return;
        }
        if (maxHeight !== undefined && height > maxHeight) {
          resolve({ isValid: false, errorMessage: `Высота изображения "${file.name}" (${height}px) превышает максимально допустимую ${maxHeight}px` });
          return;
        }
        resolve({ isValid: true });
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve({ isValid: true }); // не блокируем загрузку, если не удалось прочитать изображение (например, не изображение)
      };
      image.src = objectUrl;
    });
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (isSecondaryLoading) return;

    const newFiles = event.target.files;
    if (!newFiles?.length) return;

    const filesArray = Array.from(newFiles);
    for (const file of filesArray) {
      const validation = validateFile(file);
      if (!validation.isValid) {
        setError({ has: 1, message: validation.errorMessage || 'Ошибка валидации файла' });
        event.target.value = "";
        return;
      }

      const dimensionsValidation = await validateImageDimensions(file);
      if (!dimensionsValidation.isValid) {
        setError({ has: 1, message: dimensionsValidation.errorMessage || 'Ошибка валидации файла' });
        event.target.value = "";
        return;
      }

      if (getDuplicateOf) {
        try {
          const isDuplicate = await getDuplicateOf(file);
          if (isDuplicate) {
            setError({
              has: 1,
              message: duplicateErrorMessage || `Файл "${file.name}" совпадает с файлом, уже загруженным в другое поле`,
            });
            event.target.value = "";
            return;
          }
        } catch {
          // Хеширование недоступно/упало (например, небезопасный контекст) —
          // не блокируем загрузку, просто пропускаем проверку на дубликат
        }
      }
    }

    setUploadedFiles([...uploadedFiles, ...filesArray]);
    event.target.value = "";
    setError(null);
  };

  const handleRemoveFile = (file: File, index: number) => {
    if (isSecondaryLoading) return;

    if (isLoadingProps) return;

    const fileIdToDelete = fileIds?.[index];
    if (fileIdToDelete) {
      if (onDeleteFile) {
        onDeleteFile(fileIdToDelete);
      } else {
        clientApi.delete(`/api/questionnaire/v1/helper/file/${fileIdToDelete}`).catch(() => {});
      }
    }

    setUploadedFiles(uploadedFiles.filter(f => f !== file));
    removeNumberByIndex(index, setFileid);
    setError(null);
    setIsFirstLoadingFile(true);
  };

  // Обработчики для мобильного тултипа
  const handleTouchStart = () => {
    if (!isSecondaryLoading || !isMobile) return;

    const timer = setTimeout(() => {
      setShowMobileTooltip(true);
    }, 500); // Показываем после долгого нажатия
    setTouchTimer(timer);
  };

  const handleTouchEnd = () => {
    if (touchTimer) {
      clearTimeout(touchTimer);
      setTouchTimer(null);
    }
  };

  const handleTouchMove = () => {
    if (touchTimer) {
      clearTimeout(touchTimer);
      setTouchTimer(null);
    }
  };

  const truncateFileName = (fileName: string) => {
    try {
      if (!fileName || typeof fileName !== 'string') return '';

      if (fileName.length <= maxFileNameLength) return fileName;

      const lastDotIndex = fileName.lastIndexOf('.');

      if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
        return fileName.substring(0, maxFileNameLength - 3) + '...';
      }

      const extension = fileName.substring(lastDotIndex);
      const nameWithoutExtension = fileName.substring(0, lastDotIndex);

      if (extension.length >= maxFileNameLength - 3) {
        return '...' + extension.substring(0, maxFileNameLength - 3);
      }

      const availableNameLength = maxFileNameLength - 3 - extension.length;

      if (availableNameLength <= 0) {
        return '...' + extension;
      }

      if (nameWithoutExtension.length <= availableNameLength) {
        return nameWithoutExtension + '... ' + extension;
      }

      return nameWithoutExtension.substring(0, availableNameLength) + '... ' + extension;
    } catch (error) {
      return fileName?.length > maxFileNameLength
        ? fileName.substring(0, maxFileNameLength) + '...'
        : fileName || '';
    }
  };

  const getFileRequirementsText = () => {
    const requirements: string[] = [];

    if (allowedFileTypes && allowedFileTypes.length > 0) {
      const extensions = allowedFileTypes
        .filter(type => type.startsWith('.'))
        .map(ext => ext.substring(1).toUpperCase())
        .join(', ');

      if (extensions) {
        requirements.push(extensions);
      }
    }

    if (maxFileSize !== undefined) {
      const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);
      requirements.push(`до ${maxSizeMB} МБ`);
    }

    return requirements.length > 0 ? requirements.join(' • ') : '';
  };

  const isDisabledUploadButton =
    isDisabledButton ||
    uploadedFiles.length === count ||
    (isLoadingProps && uploadedFiles.length === count) ||
    isSecondaryLoading;

  const menuItems: MenuProps["items"] = [
    {
      key: "upload",
      label: "Загрузить файл",
      icon: <UploadOutlined />,
      onClick: () => {
        if (!isSecondaryLoading) fileInputRef.current?.click();
      },
      disabled: isSecondaryLoading,
    },
    {
      key: "camera",
      label: "Сделать фото",
      icon: <CameraOutlined />,
      onClick: () => {
        if (!isSecondaryLoading) cameraInputRef.current?.click();
      },
      disabled: isSecondaryLoading,
    },
  ];

  // Мобильный кастомный тултип
  const MobileTooltip = () => {
    if (!showMobileTooltip || !isSecondaryLoading || !isMobile) return null;

    return (
      <div
        ref={tooltipRef}
        className="mobile-tooltip"
        style={{
          position: 'absolute',
          bottom: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '11px',
          whiteSpace: 'nowrap',
          zIndex: 1000,
          marginBottom: '8px',
          maxWidth: '240px',
          textAlign: 'center',
        }}
      >
        {secondaryLoadingTooltip}
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid rgba(0, 0, 0, 0.75)',
          }}
        />
      </div>
    );
  };

  // Компонент кнопки загрузки с тултипом
  const UploadButtonWithTooltip = ({
    children,
    showTooltip,
    wrapperStyle
  }: {
    children: React.ReactNode;
    showTooltip: boolean;
    wrapperStyle?: React.CSSProperties;
  }) => {
    if (isMobile) {
      return (
        <div
          style={{
            position: 'relative',
            display: 'inline-block',
            width: '100%',
            ...wrapperStyle
          }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
        >
          {children}
          <MobileTooltip />
        </div>
      );
    }

    if (!showTooltip) {
      return <div style={{ ...wrapperStyle }}>{children}</div>;
    }

    return (
      <Tooltip title={secondaryLoadingTooltip} placement="top" open={showTooltip}>
        <div style={{ ...wrapperStyle }}>
          {children}
        </div>
      </Tooltip>
    );
  };

  const UploadButton = () => {
    const requirementsText = showRequirementsInButton ? getFileRequirementsText() : '';

    const isRow = buttonLayout === "row";

    const buttonInner = isLoadingProps ? (
      <Spin indicator={antIcon} />
    ) : (
      <div className={isRow ? "upload-button-inner-row" : "upload-button-inner-column"}>
        <span className="upload-button-icon" />
        <span className="upload-button-content-text">
          {contentText}
          {required && <span className="upload-button-required">*</span>}
        </span>
        {requirementsText && (
          <span className="upload-button-requirements">{requirementsText}</span>
        )}
      </div>
    );

    const buttonContent = (
      <button
        type="button"
        style={{ ...uploadButtonStyle }}
        className={cn("upload-button", "upload-button-dropdown", uploadButtonClassName, {
          "upload-button-disabled": isDisabledUploadButton || isSecondaryLoading,
          "upload-button-row": isRow,
        })}
        disabled={isDisabledUploadButton || isSecondaryLoading}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        {buttonInner}
      </button>
    );

    if (mode === "dropdown" && isMobile) {
      return (
        <UploadButtonWithTooltip
          showTooltip={isSecondaryLoading}
        >
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            disabled={isDisabledUploadButton || isSecondaryLoading}
            overlayStyle={isSecondaryLoading ? { pointerEvents: 'none' } : {}}
          >
            {buttonContent}
          </Dropdown>
        </UploadButtonWithTooltip>
      );
    }

    // simple mode
    const labelContent = (
      <label
        htmlFor={`file-upload${id}`}
        style={{ ...uploadLabelStyle }}
        className={cn("upload-button", uploadButtonClassName, {
          "upload-button-disabled": isDisabledUploadButton || isSecondaryLoading,
          "upload-button-row": isRow,
        })}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
      >
        {buttonInner}
      </label>
    );

    return (
      <UploadButtonWithTooltip
        showTooltip={isSecondaryLoading && !isMobile}
        wrapperStyle={{ width: "100%" }}
      >
        {labelContent}
      </UploadButtonWithTooltip>
    );
  };

  const FileList = () => (
    <>
      {uploadedFiles.map((file, index) => (
        <div className="file-info" key={index}>
          <span id="file-name" title={file.name}>{truncateFileName(file?.name)}</span>
          <button
            type="button"
            id="remove-file"
            onClick={() => handleRemoveFile(file, index)}
            disabled={isSecondaryLoading}
            className={isSecondaryLoading ? "remove-file-disabled" : ""}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchMove={handleTouchMove}
          />
        </div>
      ))}
    </>
  );

  const UploadInputs = () => {
    const acceptAttribute = allowedFileTypes && allowedFileTypes.length > 0
      ? allowedFileTypes.join(',')
      : undefined;

    return (
      <>
        <input
          ref={fileInputRef}
          disabled={isDisabledUploadButton || isSecondaryLoading}
          type="file"
          id={`file-upload${id}`}
          className="hidden"
          onChange={handleFileChange}
          accept={acceptAttribute}
        />

        <input
          ref={cameraInputRef}
          disabled={isDisabledUploadButton || isSecondaryLoading}
          type="file"
          accept={acceptAttribute || "image/*"}
          capture="environment"
          className="hidden"
          onChange={handleFileChange}
        />
      </>
    );
  };

  const UploadContainer = ({ withPrefix }: { withPrefix?: boolean }) => (
    <div id={inputIdForErrorScroll}>
      <div
        className={cn(
          { wrapperInputWithFileAttachment: withPrefix },
          "upload-container",
          uploadContainerClassName
        )}
        style={{
          ...(containerHeight && { height: containerHeight }),
          position: 'relative',
        }}
      >
        {withPrefix && prefixComponent}
        <UploadButton />
        <UploadInputs />
      </div>
      <FileList />
    </div>
  );

  return prefixComponent ? <UploadContainer withPrefix /> : <UploadContainer />;
};